<template>
  <div class="BaseText" ref="componentRef">
    <label v-if="label" :id="id + '-label'" class="text-label">{{ label }}</label>

    <v-textarea :id="id" ref="$field" :aria-labelledby="label ? id + '-label' : undefined"
      :aria-label="!label && externalLabel ? externalLabel : undefined" :placeholder="placeholder" :readonly="!editable"
      :disabled="!editable" :modelValue="value" @update:modelValue="value = $event.trim()"
      :rules="[...rules, validationRules.charactersControl]" :rows="rows" :hide-details="true" />

    <div :id="`${id}-messages`" hidden></div>

    <p v-if="maxCharacters" class="max-characters">
      {{
        t('components.textarea.characters-count', {
          count: `${currentCharacters}/${maxCharacters}`
        })
      }}
    </p>

    <ErrorMessageComponent :messages="fieldValidationErrorMessage" />
  </div>
</template>

<script setup lang="ts">
import { useRules } from '@/modules/core/composables/useRulesComposable'
import { useValidationError } from '@/modules/core/composables/validationErrorsComposable'
import type { ValidationRuleType } from '@/modules/core/models/types'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  endDuplicatedLabelControl,
  startDuplicatedLabelControl
} from '../../composables/accesibilityComposable'
import ErrorMessageComponent from '../formFields/ErrorMessageComponent.vue'

const { t } = useI18n()
const { maxValue } = useRules()

interface PropsInterface {
  id: string
  label?: string
  externalLabel?: string
  modelValue: string | null
  placeholder?: string
  editable?: boolean
  rows?: string
  maxCharacters?: number
  rules?: ValidationRuleType[]
}

const props = withDefaults(defineProps<PropsInterface>(), {
  maxCharacters: 0,
  editable: true,
  rules: () => []
})

const validationRules = {
  charactersControl: (value: string) =>
    !props.maxCharacters ||
    maxValue(value?.length, props.maxCharacters, 'common.validations.max-length')
}

const value = defineModel()

const currentCharacters = computed(() => {
  return String(value.value).length
})

const { $field, fieldValidationErrorMessage } = useValidationError()

const componentRef = ref<HTMLElement | null>(null)
let observer: MutationObserver | null = null

onMounted(() => {
  observer = startDuplicatedLabelControl(componentRef.value)
})

onUnmounted(() => {
  endDuplicatedLabelControl(observer)
})
</script>

<style lang="scss" scoped>
.BaseText {
  display: flex;
  flex-direction: column;
  gap: $gap-s;

  label {
    color: $color-neutral-dark;
  }

  .max-characters {
    display: flex;
    justify-content: flex-end;
    font-size: $font-size-s;
    color: $color-neutral-dark;
  }

  :deep(.v-input) {
    &:focus-within {
      box-shadow: 0 0 0 4px $color-focus;
    }

    &.v-input--disabled {
      .v-field__overlay {
        background: var(--color-grey-400);
      }

      &.v-textarea {
        .v-field__input {
          &:disabled {
            border-bottom: 1.5px solid $color-grey-500;
          }
        }
      }
    }
  }


  :deep(.v-field) {
    background-color: $color-grey-200;
    border-radius: $b-radius-0;
    box-shadow: $box-shadow-0;
    transition: all 0.2s ease-in-out;

    textarea {
      color: $color-neutral-dark;
    }
  }

  :deep(.v-field--error) {
    background-color: $color-error-200;

    textarea {
      color: $color-error;
    }
  }

  :deep(.v-field:focus-within) {
    textarea {
      &:focus,
      &:focus-visible {
        outline: none;
        box-shadow: none;
      }
    }
  }
}
</style>
